/**
 * The trail from All regions down to where you are (BON-534).
 *
 * Rendered at every screen size, and independently of whether the region has
 * sub-regions — in the design prototype the trail lived inside the sub-region
 * grid, so a leaf region lost its only in-page way back up.
 */
import { Link } from 'react-router-dom';

import { OVERVIEW_ALL_REGIONS_HREF } from './overview-all-regions';
import type { OverviewRegionAncestor, OverviewRegionRow } from './overview-types';

export interface RegionBreadcrumbProps {
  region: OverviewRegionRow;
  /** Top-level first, immediate parent last. Empty at the top level. */
  ancestors: OverviewRegionAncestor[];
}

interface Step {
  key: string;
  label: string;
  /** Absent on the last step — you do not link to where you already are. */
  href?: string;
}

const separator = (
  <span aria-hidden="true" style={{ color: 'var(--nb-faint)', fontSize: 11 }}>
    ›
  </span>
);

export function RegionBreadcrumb({ region, ancestors }: RegionBreadcrumbProps) {
  const steps: Step[] = [
    { key: 'all', label: 'All regions', href: OVERVIEW_ALL_REGIONS_HREF },
    ...ancestors.map((a) => ({
      key: a.id,
      label: a.name,
      href: `/admin/overview/${a.path.join('/')}`,
    })),
    { key: region.id, label: region.name },
  ];

  return (
    <nav
      aria-label="Region trail"
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '0.3rem',
        marginBottom: '0.65rem',
      }}
    >
      {steps.map((step, index) => (
        <span key={step.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          {index > 0 && separator}
          {step.href ? (
            <Link
              to={step.href}
              data-testid={`crumb-${step.key}`}
              style={{
                fontSize: 12.5,
                fontWeight: 600,
                color: 'var(--nb-primary)',
                textDecoration: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              {step.label}
            </Link>
          ) : (
            <span
              data-testid={`crumb-${step.key}`}
              aria-current="page"
              style={{
                fontSize: 12.5,
                fontWeight: 700,
                color: 'var(--nb-ink)',
                whiteSpace: 'nowrap',
              }}
            >
              {step.label}
            </span>
          )}
        </span>
      ))}
    </nav>
  );
}
